/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { LEADERBOARD_DATA } from '../data/leaderboard';
import { formatCompact, formatAddress } from '../lib/utils/format';
import { Trophy, ShieldCheck, Star, Gem, PenTool, ArrowUp, ArrowDown, Activity, Crown } from 'lucide-react';

const badgeIcons: { [key: string]: React.ReactNode } = {
    'Whale': <ShieldCheck size={12} className="text-blue-500" />,
    'OG': <Star size={12} className="text-nobel-gold" />,
    'Diamond Hands': <Gem size={12} className="text-purple-500" />,
    'Creator': <PenTool size={12} className="text-green-500" />,
    'Early': <Star size={12} className="text-nobel-gold" />
};

interface LeaderboardProps {
    onAnalyzeWallet: (wallet: string) => void;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({ onAnalyzeWallet }) => {
    const allUsers = LEADERBOARD_DATA;
    const top3 = allUsers.slice(0, 3);
    const rest = allUsers.slice(3);

    const rankStyles: { [key: number]: string } = {
        1: "bg-gradient-to-b from-amber-50 to-white border-amber-300 order-2 lg:order-2 z-20 shadow-soft-2xl ring-4 ring-amber-100/50 scale-105", // Gold
        2: "bg-gradient-to-b from-stone-50 to-white border-stone-300 order-1 lg:order-1 shadow-soft-xl", // Silver
        3: "bg-gradient-to-b from-orange-50 to-white border-orange-300 order-3 lg:order-3 shadow-soft-xl", // Bronze
    };

    const rankMedalColor: { [key: number]: string } = {
        1: "bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 text-white border-amber-200",
        2: "bg-gradient-to-br from-stone-300 via-stone-400 to-stone-500 text-white border-stone-200",
        3: "bg-gradient-to-br from-orange-300 via-orange-400 to-orange-500 text-white border-orange-200",
    };

    return (
        <section className="py-24 bg-[#F9F8F4]">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <span className="text-xs font-bold tracking-widest text-nobel-gold uppercase mb-2 block">Top Traders</span>
                    <h2 className="font-serif text-4xl text-stone-900 mb-4">Platform Leaderboard</h2>
                    <p className="text-stone-500">The top market movers on HotLaunchpad, ranked by total volume and activity.</p>
                </div>

                {/* Top 3 Podium */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16 max-w-5xl mx-auto items-end pb-8">
                    {top3.map((user) => (
                        <div 
                            key={user.rank} 
                            className={`w-full p-8 rounded-3xl border flex flex-col items-center relative transition-all duration-300 h-full hover:-translate-y-2 ${rankStyles[user.rank] || 'bg-white'}`}
                        >
                            {user.rank === 1 && (
                                <div className="absolute -top-10 text-nobel-gold">
                                    <Crown size={48} fill="currentColor" />
                                </div>
                            )}
                            <div className={`w-20 h-20 rounded-full mb-6 flex items-center justify-center font-bold text-2xl border-4 shadow-lg ${rankMedalColor[user.rank] || 'bg-stone-100 text-stone-400'}`}>
                                #{user.rank}
                            </div>
                            
                            <h3 
                                onClick={() => onAnalyzeWallet(user.walletAddress)}
                                className="font-bold text-xl text-stone-900 mb-2 cursor-pointer hover:text-nobel-gold transition-colors font-serif"
                            >
                                {formatAddress(user.walletAddress, 6)}
                            </h3>
                            
                            <div className="flex flex-wrap justify-center gap-2 mb-8">
                                {user.badges.map(b => (
                                    <span key={b} className="text-[10px] px-2.5 py-1 rounded-full bg-white/50 border border-stone-200 font-bold text-stone-600 flex items-center gap-1 shadow-sm">
                                        {badgeIcons[b]} {b}
                                    </span>
                                ))}
                            </div>

                            <div className="w-full space-y-4 mt-auto">
                                <div className="flex justify-between items-center p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-stone-200/50 shadow-sm">
                                    <span className="text-xs text-stone-500 font-bold uppercase tracking-wider">Volume</span>
                                    <span className="font-mono font-bold text-lg text-stone-900">${formatCompact(user.totalVolume)}</span>
                                </div>
                                <div className="flex justify-between items-center p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-stone-200/50 shadow-sm">
                                    <span className="text-xs text-stone-500 font-bold uppercase tracking-wider">Launches</span>
                                    <span className="font-mono font-bold text-lg text-stone-900">{user.tokensLaunched}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Ranks 4+ List */}
                <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-stone-200 shadow-soft-lg overflow-hidden">
                    <div className="grid grid-cols-12 gap-4 p-5 border-b border-stone-100 text-xs font-bold text-stone-400 uppercase tracking-wider bg-stone-50/50">
                        <div className="col-span-1 text-center">Rank</div>
                        <div className="col-span-5">Trader</div>
                        <div className="col-span-3 text-right">Volume</div>
                        <div className="col-span-3 text-right">Score</div>
                    </div>
                    <div>
                        {rest.map((user) => (
                            <div key={user.rank} className="grid grid-cols-12 gap-4 p-5 items-center hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-0 group">
                                <div className="col-span-1 text-center font-bold text-stone-400 group-hover:text-stone-900 text-lg">#{user.rank}</div>
                                <div className="col-span-5 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center text-xs font-bold text-stone-500 border border-stone-200 group-hover:bg-white group-hover:shadow-sm transition-all">
                                        {user.walletAddress.slice(0, 2)}
                                    </div>
                                    <div>
                                        <div 
                                            onClick={() => onAnalyzeWallet(user.walletAddress)}
                                            className="font-bold text-sm text-stone-900 cursor-pointer hover:text-nobel-gold transition-colors"
                                        >
                                            {formatAddress(user.walletAddress, 6)}
                                        </div>
                                        <div className="flex gap-1 mt-1">
                                            {user.badges.slice(0, 3).map(b => (
                                                <span key={b} className="text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded border border-stone-200">{b}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-3 text-right font-mono text-sm font-bold text-stone-900">${formatCompact(user.totalVolume)}</div>
                                <div className="col-span-3 text-right">
                                    <div className="flex items-center justify-end gap-1.5 font-mono text-sm font-bold text-stone-600">
                                        {user.points.toLocaleString()}
                                        {user.change24h && user.change24h > 0 ? <ArrowUp size={14} className="text-green-500" /> : null}
                                        {user.change24h && user.change24h < 0 ? <ArrowDown size={14} className="text-red-500" /> : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};